import type React from 'react';
import { type CalendarDay } from '../../types/tasks';

interface TaskCalendarProps {
  days: CalendarDay[];
  className?: string;
  onDayClick?: (day: CalendarDay) => void;
  variant?: 'week' | 'month';
}

export const TaskCalendar: React.FC<TaskCalendarProps> = ({
  days,
  className = '',
  onDayClick,
  variant = 'week'
}) => {
  const getDayName = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const getDayNumber = (dateString: string): number => {
    const date = new Date(dateString);
    return date.getDate();
  };

  const getProgressColor = (progress: number): string => {
    if (progress >= 90) {return 'bg-green-500';}
    if (progress >= 70) {return 'bg-blue-500';}
    if (progress >= 50) {return 'bg-yellow-500';}
    if (progress >= 30) {return 'bg-orange-500';}
    if (progress > 0) {return 'bg-red-500';}
    return 'bg-gray-200';
  };

  const getProgressText = (progress: number): string => {
    if (progress >= 90) {return 'Excellent';}
    if (progress >= 70) {return 'Good';}
    if (progress >= 50) {return 'Fair';}
    if (progress >= 30) {return 'Poor';}
    if (progress > 0) {return 'Started';}
    return 'No progress';
  };

  const isToday = (dateString: string): boolean => {
    const [today] = new Date().toISOString().split('T');
    return dateString === today;
  };

  const isWeekend = (dateString: string): boolean => {
    const date = new Date(dateString);
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  if (variant === 'week') {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Progress</h3>
        <div className="grid grid-cols-7 gap-2">
          {days.map((day) => (
            <div
              key={day.date}
              className={`
                text-center p-3 rounded-lg border-2 transition-all duration-200
                ${onDayClick ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-50' : ''}
                ${isToday(day.date) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                ${isWeekend(day.date) ? 'bg-gray-50' : ''}
              `}
              onClick={() => onDayClick?.(day)}
              role={onDayClick ? 'button' : undefined}
              tabIndex={onDayClick ? 0 : undefined}
              aria-label={`${getDayName(day.date)}, ${day.progress}% complete`}
            >
              <div className="text-xs font-medium text-gray-600 mb-1">
                {getDayName(day.date)}
              </div>
              <div className={`text-lg font-bold mb-2 ${isToday(day.date) ? 'text-blue-600' : 'text-gray-900'}`}>
                {getDayNumber(day.date)}
              </div>
              
              {/* Progress Circle */}
              <div className="relative w-12 h-12 mx-auto mb-2">
                <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="text-gray-200"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="transparent"
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className={getProgressColor(day.progress).replace('bg-', 'text-')}
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="transparent"
                    strokeDasharray={`${day.progress}, 100`}
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-gray-700">
                    {day.progress}%
                  </span>
                </div>
              </div>
              
              <div className="text-xs text-gray-500">
                {day.completedTasks}/{day.totalTasks} tasks
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Month variant - compact grid view
  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Progress</h3>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 p-2">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => (
          <div
            key={day.date}
            className={`
              aspect-square flex flex-col items-center justify-center p-1 rounded transition-all duration-200
              ${onDayClick ? 'cursor-pointer hover:bg-gray-100' : ''}
              ${isToday(day.date) ? 'bg-blue-100 border border-blue-300' : ''}
            `}
            onClick={() => onDayClick?.(day)}
            role={onDayClick ? 'button' : undefined}
            tabIndex={onDayClick ? 0 : undefined}
            title={`${getDayNumber(day.date)}: ${day.progress}% complete (${day.completedTasks}/${day.totalTasks} tasks)`}
          >
            <div className={`text-xs font-medium mb-1 ${isToday(day.date) ? 'text-blue-600' : 'text-gray-700'}`}>
              {getDayNumber(day.date)}
            </div>
            <div
              className={`w-4 h-4 rounded-full ${getProgressColor(day.progress)}`}
              title={getProgressText(day.progress)}
            >
              {day.progress > 0 && (
                <div className="w-full h-full flex items-center justify-center">
                  {day.progress === 100 && (
                    <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-gray-200 rounded-full" />
          <span>No tasks</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500 rounded-full" />
          <span>0-30%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-yellow-500 rounded-full" />
          <span>50-70%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded-full" />
          <span>90-100%</span>
        </div>
      </div>
    </div>
  );
};